/**
 * useNotebookPolling (Intelligence Studio)
 *
 * SRP hook: poll notebook status while processing and merge server files with local failed files.
 * Extracted (copy/paste) from containers/IntelligenceStudioPage.jsx.
 *
 * ✅ ENTERPRISE: Now includes stuck detection, auto-recovery, and exponential backoff.
 */

import { useEffect, useRef, useCallback } from 'react';
import { authService } from '../../../services/auth/AuthService';
import { getApiBaseUrl } from '../../../config/runtimeUrls';
import { getAuthToken } from '../../../utils/auth';
import { normalizeUniversalError } from '@/pages/intelligence-studio/utils/universalError';
import { traceFetch } from '@/pages/intelligence-studio/utils/traceContext';
import { applyVaultStateToNotebook } from '@/pages/intelligence-studio/utils/vaultState';

// ✅ ENTERPRISE: Polling constants
const INITIAL_POLL_INTERVAL_MS = 2000; // 2 seconds
const MAX_POLL_INTERVAL_MS = 15000; // 15 seconds (exponential backoff cap)
const STUCK_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes = definitely stuck
const MAX_CONSECUTIVE_ERRORS = 5;
const STATE_RESOLVER_ENABLED = process.env.REACT_APP_IS_STATE_RESOLVER !== 'false';

export default function useNotebookPolling({
  selectedNotebook,
  setSelectedNotebook,
  setNotebooks,
  intelligenceStudioApi,
}) {
  const pollCountRef = useRef(0);
  const consecutiveErrorsRef = useRef(0);
  const lastUpdateTimeRef = useRef(Date.now());

  // ✅ ENTERPRISE: Auto-finalize stuck notebooks
  const tryAutoFinalize = useCallback(async notebookId => {
    try {
      console.log(`🔧 [Polling] Attempting auto-finalize for stuck notebook: ${notebookId}`);
      const authToken = authService?.getAccessToken?.() || getAuthToken() || '';
      const apiBase = String(getApiBaseUrl() || '').replace(/\/+$/, '');
      const response = await traceFetch(
        `${apiBase}/intelligence-studio/notebooks/${notebookId}/finalize`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${authToken}`,
          },
        },
        {
          apiName: 'notebook.finalize',
          pipelineStage: 'http',
          notebookId,
        }
      );
      const result = await response.json();
      return result?.finalized === true;
    } catch (err) {
      console.warn(`⚠️ [Polling] Auto-finalize failed:`, err.message);
      return false;
    }
  }, []);

  // ✅ FIX: Proper polling with stop condition + PRESERVE LOCAL FILES
  useEffect(() => {
    const status = selectedNotebook?.status;
    const vaultState = selectedNotebook?._vaultState?.vaultState || selectedNotebook?._vaultState?.data?.vaultState;
    const sseConnected = selectedNotebook?._progressStreamConnected === true;
    const lastSseEventAt =
      typeof selectedNotebook?._lastSseEventAt === 'number' ? selectedNotebook._lastSseEventAt : 0;
    const sseStale = sseConnected && (!lastSseEventAt || Date.now() - lastSseEventAt > 15000);
    // ✅ ENTERPRISE: Poll is a fallback only. If SSE progress stream is connected, disable polling to avoid races.
    const fallbackProcessing = status === 'processing' || status === 'uploading';
    const shouldPoll =
      (STATE_RESOLVER_ENABLED
        ? (vaultState ? vaultState === 'INGESTING' : fallbackProcessing)
        : fallbackProcessing) &&
      (!sseConnected || sseStale);

    if (!selectedNotebook?.id || !shouldPoll) {
      // Reset counters when not polling
      pollCountRef.current = 0;
      consecutiveErrorsRef.current = 0;
      return; // Only poll if processing
    }

    console.log('[IntelligenceStudio] Starting poll - status:', status);
    lastUpdateTimeRef.current = Date.now();

    // ✅ ENTERPRISE: Use exponential backoff for polling
    const getNextInterval = () => {
      const baseInterval = INITIAL_POLL_INTERVAL_MS;
      const multiplier = Math.min(Math.pow(1.5, Math.floor(pollCountRef.current / 5)), 7.5);
      return Math.min(baseInterval * multiplier, MAX_POLL_INTERVAL_MS);
    };

    let timeoutId = null;

    const poll = async () => {
      pollCountRef.current += 1;

      try {
        const response = await intelligenceStudioApi.getNotebook(selectedNotebook.id);
        consecutiveErrorsRef.current = 0; // Reset on success

        // ✅ FIX: Robust parsing - apiClient returns {success, data}
        let serverNotebook = null;
        if (response?.data?.id) {
          serverNotebook = response.data;
        } else if (response?.id) {
          serverNotebook = response;
        }

        if (!serverNotebook) {
          console.warn('[IntelligenceStudio] Poll returned invalid data');
          timeoutId = setTimeout(poll, getNextInterval());
          return;
        }

        // ✅ Phase D: Attach canonical vault state (feature-flagged)
        let resolvedStateData = null;
        if (STATE_RESOLVER_ENABLED) {
          try {
            const stateResponse = await intelligenceStudioApi.getNotebookState(selectedNotebook.id);
            const stateData =
              stateResponse?.data?.vaultState
                ? stateResponse.data
                : stateResponse?.data?.data?.vaultState
                  ? stateResponse.data.data
                  : stateResponse?.vaultState
                    ? stateResponse
                    : null;
            resolvedStateData = stateData || null;
            if (resolvedStateData) {
              serverNotebook = applyVaultStateToNotebook(serverNotebook, resolvedStateData);
            } else if (serverNotebook?._vaultState || selectedNotebook?._vaultState) {
              serverNotebook = applyVaultStateToNotebook(
                serverNotebook,
                serverNotebook?._vaultState || selectedNotebook?._vaultState
              );
            }
          } catch (stateErr) {
            console.warn('[IntelligenceStudio] Vault state poll failed:', stateErr?.message || stateErr);
          }
        }

        // ✅ ENTERPRISE: Detect if notebook is stuck
        const timeSinceLastUpdate = Date.now() - lastUpdateTimeRef.current;
        const resolvedVaultState = serverNotebook?._vaultState?.vaultState || null;
        const isStuck =
          (STATE_RESOLVER_ENABLED ? resolvedVaultState === 'INGESTING' : serverNotebook.status === 'processing') &&
          timeSinceLastUpdate > STUCK_THRESHOLD_MS;

        if (isStuck) {
          console.warn(
            `⚠️ [Polling] Notebook ${selectedNotebook.id} appears stuck (${Math.round(timeSinceLastUpdate / 1000)}s without progress)`
          );

          // Attempt auto-finalize
          const finalized = await tryAutoFinalize(selectedNotebook.id);
          if (finalized) {
            // Re-fetch after finalization
            const refreshed = await intelligenceStudioApi.getNotebook(selectedNotebook.id);
            serverNotebook = refreshed?.data?.id
              ? refreshed.data
              : refreshed?.id
                ? refreshed
                : serverNotebook;
            if (resolvedStateData) {
              serverNotebook = applyVaultStateToNotebook(serverNotebook, resolvedStateData);
            }
            console.log(`✅ [Polling] Auto-finalized, new status: ${serverNotebook.status}`);
          } else {
            // Mark as stuck for UI
            serverNotebook._isStuck = true;
            serverNotebook._stuckMessage =
              'Processing appears stuck. Click "Fix Stuck Vault" to resolve.';
          }
        }

        // Track progress changes
        if (
          serverNotebook.processingProgress !== selectedNotebook?.processingProgress ||
          serverNotebook.status !== selectedNotebook?.status
        ) {
          lastUpdateTimeRef.current = Date.now();
        }

        console.log(
          '[IntelligenceStudio] Poll - status:',
          serverNotebook.status,
          'isReady:',
          serverNotebook.isReady,
          'files:',
          serverNotebook.files?.length
        );

        // ✅ Phase D: Server files are authoritative (no local merges)
        const prevEpoch = Number(selectedNotebook?.vaultEpoch || 0);
        const nextEpoch = Number(serverNotebook?.vaultEpoch || 0);
        const isNewerOrEqual = !nextEpoch || !prevEpoch || nextEpoch >= prevEpoch;
        if (isNewerOrEqual) {
          setSelectedNotebook(serverNotebook);
          setNotebooks(prev => prev.map(n => (n.id === serverNotebook.id ? serverNotebook : n)));
        }

        // ✅ STOP CONDITION
        if (
          (STATE_RESOLVER_ENABLED
            ? serverNotebook?._vaultState?.vaultState !== 'INGESTING'
            : serverNotebook.status !== 'processing' && serverNotebook.status !== 'uploading') ||
          serverNotebook.isReady
        ) {
          console.log('[IntelligenceStudio] Stopping poll - notebook ready or not processing');
          return; // Don't schedule next poll
        }

        // Schedule next poll with exponential backoff
        timeoutId = setTimeout(poll, getNextInterval());
      } catch (err) {
        console.error('[IntelligenceStudio] Poll error:', err);
        consecutiveErrorsRef.current += 1;

        // ✅ ENTERPRISE: Stop polling after too many consecutive errors
        if (consecutiveErrorsRef.current >= MAX_CONSECUTIVE_ERRORS) {
          console.error(
            `[IntelligenceStudio] Stopping poll after ${MAX_CONSECUTIVE_ERRORS} consecutive errors`
          );
          const uiErr = normalizeUniversalError(err, {
            source: 'polling',
            fallbackCode: 'POLLING_FAILED',
            fallbackMessage: 'Failed to check vault status. Please refresh the page.',
          });
          setSelectedNotebook(prev => ({
            ...prev,
            _pollError: true,
            _errorMessage: 'Failed to check vault status. Please refresh the page.',
            _lastPollError: uiErr,
          }));
          return;
        }

        // Continue polling with longer interval on error
        timeoutId = setTimeout(poll, MAX_POLL_INTERVAL_MS);
      }
    };

    // Start polling
    timeoutId = setTimeout(poll, INITIAL_POLL_INTERVAL_MS);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    selectedNotebook?.id,
    selectedNotebook?.status,
    selectedNotebook?._progressStreamConnected,
    tryAutoFinalize,
  ]); // Re-run when status enters/leaves processing
}
